// src/features/editor/components/studio/useBlockDraft.ts
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { skipToken } from '@reduxjs/toolkit/query/react'
import type { Block } from '@/types/blocks'
import { lessonsApi, useGetLessonBlocksQuery } from '@/features/editor/lessonsApi'
import { useDeleteBlockMutation, useUpdateBlockMutation } from '@/features/editor/blocksApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  remapSelection,
  selectBlock,
  setBlockSheetOpen,
  setSaveStatus,
} from '@/features/editor/editorSlice'
import { moderationApi } from '@/features/moderation/moderationApi'
import { toastedByMiddleware } from '@/features/editor/hooks/useSaveStatusWrap'
import { parseBlockError } from '@/features/editor/lib/parseBlockError'
import { designTextOverLimit } from '@/features/editor/lib/designHtml'
import { toBlockCreate } from '@/features/editor/lib/duplicateBlock'
import { normalizeArticleBlock } from '@/features/editor/lib/articleBlock'
import { blockSignature, contentSignature } from '@/features/editor/lib/blockSignature'
import { acceptEcho, pickServerBlock, syncDraft } from './blockDraftSync'
import { useDraftHistory, type DraftHistoryResult } from './useDraftHistory'
import { isBlockLocked, refusedByBlockLock, useBlocksInRun } from './useBlocksInRun'
import { blockLeftOf } from '@/features/editor/lib/blockLeftOf'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

/**
 * Наследует `DraftHistoryResult` — `canUndo`/`canRedo`/`undo`/`redo`, шаги
 * истории правок ОТКРЫТОГО блока (п.11, глубина 10). На сервер они ничего не
 * шлют: меняется только черновик, сохранение остаётся за кнопкой «Сохранить».
 */
export interface BlockDraftResult extends DraftHistoryResult {
  block: Block | null
  draft: Block | null
  setDraft: React.Dispatch<React.SetStateAction<Block | null>>
  isDirty: boolean
  isSaving: boolean
  /** I-2: серверный блок изменился, пока черновик автора расходился с ним —
   *  сохранение затрёт чужую (оплаченную) запись. Не запрет, а предупреждение:
   *  область прогона отрисовки считает сервер, точного лока тут быть не может. */
  hasRunConflict: boolean
  /** Заменить черновик серверным блоком. Жмёт АВТОР — авто-отката нет. */
  takeServerVersion: () => void
  doSave: () => Promise<void>
  doDelete: () => Promise<boolean>
}

const REREAD = { forceRefetch: true, subscribe: false } as const
/** Сколько после своего сохранения приходящий блок считается его эхом (рефетч). */
const OWN_ECHO_MS = 5000

// §23: courseId обязателен — ради инвалидации Course на удаление (иначе
// бейдж плашки урока не обновится). Опциональность держала бы инвариант на
// дисциплине вызывающего, а не на типах.
export const useBlockDraft = (courseId: number): BlockDraftResult => {
  const dispatch = useAppDispatch()
  const selectedLessonId = useAppSelector((s) => s.editor.selectedLessonId)
  const selectedBlockId = useAppSelector((s) => s.editor.selectedBlockId)
  const probeCourseId = useAppSelector((s) => s.editor.draftProbeCourseId)
  const idAliases = useAppSelector((s) => s.editor.idAliases)

  const { data: blocks = [] } = useGetLessonBlocksQuery(selectedLessonId ?? skipToken)

  const [updateBlock, { isLoading: isSaving }] = useUpdateBlockMutation()
  const [deleteBlock] = useDeleteBlockMutation()

  const [draft, setDraft] = useState<Block | null>(() =>
    pickServerBlock(blocks, selectedBlockId, idAliases, null),
  )

  // Last server block this effect has seen for the current id, so we can tell
  // a real unsaved edit apart from AI-rewritten content the author hasn't
  // seen yet (E7). Comparing the draft only to the LATEST block (as before)
  // can't make that distinction: a paid job (block-edit or image) rewrites
  // the block server-side, `JobPoller` invalidates `Block`, a new object
  // arrives with the same id — and the old check kept the stale draft,
  // making the panel show pre-rewrite content with "Save" lit up. Clicking
  // it then PUTs the stale body and silently reverts the paid rewrite.
  // Снимок обновляется ТОЛЬКО когда черновик с ним синхронизируется (принят
  // серверный блок, сменился выбор, прошло сохранение). Пока автор держит
  // расхождение, снимок остаётся его ОСНОВОЙ — по ней и видно, что сервер уехал
  // из-под него (I-2).
  const lastServerBlockRef = useRef<Block | null>(draft)
  // Основа, которую поставило СВОЁ сохранение: историю правок она не обнуляет —
  // при автосохранении иначе Cmd+Z не переживал бы и секунды.
  const ownSavedRef = useRef<Block | null>(null)
  const ownSavedAtRef = useRef(0)
  /** Сколько своих сохранений сейчас в полёте. */
  const ownBusyRef = useRef(0)
  // Последний отрисованный черновик: ответ сохранения сверяется с ним, а не с
  // замыканием, которое помнит черновик на момент отправки.
  const draftRef = useRef(draft)
  draftRef.current = draft
  // Серверный блок выделения; снимок читается во время рендера тем же приёмом,
  // что `hasRunConflict` ниже (см. `pickServerBlock` — отставший список урока).
  const block = pickServerBlock(blocks, selectedBlockId, idAliases, lastServerBlockRef.current)

  // Sync draft with server block on selection change OR when block first loads.
  // Cache refetches (same id, new ref) preserve user's local edits — except
  // `order`, which is managed by reordering, not by the draft: a stale draft
  // order would overwrite the new position on the next save.
  useEffect(() => {
    // Присвоение снимка живёт ВНУТРИ апдейтера: решение принимается по `prev`,
    // которого снаружи нет (эффект намеренно не зависит от `draft`, иначе он
    // перезапускался бы на каждое нажатие клавиши). Апдейтер от этого не
    // перестаёт быть идемпотентным: повторный вызов с тем же `prev` присвоит
    // то же значение.
    setDraft((prev) => {
      const base = lastServerBlockRef.current
      // Эхо СВОЕГО сохранения: рефетч после PUT приносит тот же блок в форме GET,
      // и при допечатанном тексте это выглядело бы как «AI изменил блок» (ложная
      // плашка конфликта). Свежая основа своей записи принимается молча.
      // Сюда же — оптимистичная подстановка тела В ПОЛЁТЕ запроса (`updateBlock`
      // кладёт отправленное в кеш сразу): допечатанное за этот миг иначе тоже
      // выглядело бы конфликтом.
      const echo =
        block !== null &&
        base !== null &&
        block.id === base.id &&
        (ownBusyRef.current > 0 || Date.now() - ownSavedAtRef.current < OWN_ECHO_MS)
      const next = echo && prev ? acceptEcho(prev, block, base) : syncDraft(prev, block, base)
      lastServerBlockRef.current = next.base
      if (echo) ownSavedRef.current = next.base
      return next.draft
    })
  }, [block])

  // Трогать блок, который прямо сейчас переписывает прогон, нельзя ничем —
  // включая откат правки (§6.2). Своей копии условия нет: тот же `isBlockLocked`,
  // что у панели, миниатюры и реордера.
  const blocksInRun = useBlocksInRun(courseId)
  const isRunLocked = block !== null && isBlockLocked(blocksInRun, block.id)

  // История правок открытого блока (п.11). Её ОСНОВА — `lastServerBlockRef`, и
  // читается она во время рендера намеренно, тем же приёмом, что и в
  // `hasRunConflict` ниже: снимок меняется только вместе с черновиком, то есть
  // в том же коммите. Смена основы (выбран другой блок, принята серверная
  // версия, прошло сохранение) историю обнуляет — иначе Undo воскресил бы тело
  // до платной правки прогона и молча её откатил.
  const history = useDraftHistory(
    draft,
    setDraft,
    lastServerBlockRef.current,
    isRunLocked,
    ownSavedRef.current,
  )

  const isDirty = useMemo(
    () => draft !== null && block !== null && blockSignature(draft) !== blockSignature(block),
    [draft, block],
  )

  /**
   * I-2. Конфликт — это ДВА факта разом: автор разошёлся со своей основой И
   * сервер уехал из-под этой же основы. Одного «черновик грязен» мало (обычный
   * рефетч не конфликт), одного «сервер прислал новый объект» — тоже (нетронутый
   * черновик принимает перепись молча).
   *
   * Читается ref, а не state, намеренно: снимок меняется только вместе с
   * черновиком, то есть в том же коммите, что и перерисовка.
   */
  const hasRunConflict = useMemo(() => {
    const base = lastServerBlockRef.current
    if (!draft || !block || !base || draft.id !== block.id || base.id !== block.id) return false
    return (
      contentSignature(draft) !== contentSignature(base) &&
      contentSignature(base) !== contentSignature(block)
    )
  }, [draft, block])

  // Взять версию прогона. Автор жмёт сам — молчаливого отката его текста нет.
  const takeServerVersion = () => {
    if (!block) return
    lastServerBlockRef.current = block
    setDraft(block)
  }

  // Первое сохранение живого блока завело черновик курса: бэк вернул блок
  // черновика (новый `id`, `originId` — прежний). Выделение переезжает на него
  // сразу, а `/moderation` перечитывается, чтобы курс перечитался из черновика.
  // Пока зонд стоит на этом курсе, то же после КАЖДОЙ записи делает `draftProbeMiddleware`
  // (`updateBlock` в `CONTENT_WRITE_ENDPOINTS`), поэтому свой `initiate` не шлём: по сети
  // запрос был бы один и так (RTK склеил бы его с летящим), лишний только сам dispatch.
  const followSavedIntoDraft = (fromId: number, toId: number) => {
    dispatch(remapSelection({ lessons: [], blocks: [[fromId, toId]] }))
    if (probeCourseId === courseId) return
    void dispatch(moderationApi.endpoints.getCourseModeration.initiate(courseId, REREAD))
  }

  const doSave = async () => {
    if (!draft || selectedLessonId === null || !isDirty) return
    if (refusedByBlockLock(blocksInRun, draft.id)) return
    // Заведомый 422: черновик остаётся у автора, а не уходит в отказ.
    if (designTextOverLimit(draft)) {
      dispatch(setSaveStatus('error'))
      toast.error(messagesNow(messages).designTextTooLong, { id: 'design-text-too-long' })
      return
    }
    dispatch(setSaveStatus('saving'))
    ownBusyRef.current += 1
    try {
      // toBlockCreate(draft, draft.order) — статья идёт через toArticleCreate
      // (снимает вложенный article/available/completed/isCorrect, иначе PUT
      // падает 422), а order передан явно вторым аргументом: PUT блока — полная
      // замена, и order обязан доехать до сервера, а не сброситься.
      const rawSaved = await updateBlock({
        id: draft.id,
        lessonId: selectedLessonId,
        body: toBlockCreate(draft, draft.order),
      }).unwrap()
      // Ответ мутации — такая же граница api, как и ответ getLessonBlocks: если PUT
      // блока статьи возвращает ту же форму, что GET (вложенный article, без
      // articleId верхнего уровня), сырой ответ увёл бы черновик обратно в баг
      // п.27 сразу после сохранения — isDirty ложно вспыхнул бы до рефетча.
      const saved = normalizeArticleBlock(rawSaved)
      // Sync draft with server response so normalizations (e.g. '' → null)
      // don't flip isDirty back to true after the invalidation refetch.
      // Снимок — тем же ответом: автор осознанно выбрал свою версию, и
      // расхождение с прежней основой закрыто (I-2).
      // Автосохранение: пока запрос летел, автор мог напечатать ещё или уйти на
      // другой блок. Ответ становится черновиком, только если черновик тот же,
      // что ушёл; иначе остаётся авторский (он расходится с новой основой и
      // уйдёт следующим сохранением), а у чужого блока ничего не трогаем.
      const sent = draft
      if (draftRef.current?.id === sent.id) {
        lastServerBlockRef.current = saved
        ownSavedRef.current = saved
        ownSavedAtRef.current = Date.now()
        setDraft((prev) => (prev === sent ? saved : prev))
        // Ответ сразу в кеш урока: до рефетча серверный блок оставался бы
        // ДО-сохранённым, и допечатанное во время запроса выглядело бы как
        // «блок изменился на сервере» (ложная плашка конфликта).
        if (saved.id === sent.id) {
          dispatch(
            lessonsApi.util.updateQueryData('getLessonBlocks', selectedLessonId, (list) => {
              const i = list.findIndex((b) => b.id === saved.id)
              if (i !== -1) list[i] = saved
            }),
          )
        }
      }
      if (saved.id !== draft.id) followSavedIntoDraft(draft.id, saved.id)
      dispatch(setSaveStatus('saved'))
    } catch (e) {
      dispatch(setSaveStatus('error'))
      if (!toastedByMiddleware(e))
        toast.error(parseBlockError(e, messagesNow(messages).saveBlockFailed))
    } finally {
      ownBusyRef.current -= 1
    }
  }

  /** `true` — блок удалён. */
  const doDelete = async (): Promise<boolean> => {
    if (!block || selectedLessonId === null || refusedByBlockLock(blocksInRun, block.id)) {
      return false
    }
    // Выделение сразу переходит на блок слева (удалили первый — на урок), не
    // дожидаясь ответа: панель не висит над блоком, который уходит.
    dispatch(selectBlock(blockLeftOf(blocks, block.id)))
    // Шторка телефона была про удалённый экран: открытой она показала бы соседний, которого
    // автор не выбирал (у составного блока — одну шапку).
    dispatch(setBlockSheetOpen(false))
    try {
      dispatch(setSaveStatus('saving'))
      await deleteBlock({ id: block.id, lessonId: selectedLessonId, courseId }).unwrap()
      dispatch(setSaveStatus('saved'))
      return true
    } catch (e) {
      dispatch(setSaveStatus('error'))
      if (!toastedByMiddleware(e)) toast.error(messagesNow(messages).deleteBlockFailed)
      return false
    }
  }

  return {
    block,
    draft,
    setDraft,
    isDirty,
    isSaving,
    hasRunConflict,
    takeServerVersion,
    ...history,
    doSave,
    doDelete,
  }
}
